/**
 * 🔊 Synth fallback recipes + shared master limiter. Live gameplay uses
 * the selected samples in sfxSamples when ready; the offline synth
 * audit and /sfx comparison retain these originals. WebAudio schedules
 * future events natively; no setTimeout drift).
 */
export interface BlipSpec {
  freqFrom: number
  freqTo: number
  /** Seconds of audible envelope. */
  duration: number
  type: OscillatorType
  volume: number
  /** Offset from the verb's trigger, ms. */
  delayMs: number
}

const b = (freqFrom: number, freqTo: number, duration: number, type: OscillatorType, volume: number, delayMs = 0): BlipSpec =>
  ({ freqFrom, freqTo, duration, type, volume, delayMs })

export const SFX_RECIPES: Record<string, BlipSpec[]> = {
  /** Neutral UI tap: opening things, backing out. */
  tap: [b(760, 760, 0.05, 'triangle', 0.08)],
  /** Picking something up / turning something on. */
  select: [b(500, 820, 0.08, 'sine', 0.1)],
  /** Putting something back / turning something off. */
  deselect: [b(680, 400, 0.08, 'sine', 0.1)],
  /** Committing: confirm buttons, dispatch, reward chosen. */
  confirm: [b(620, 620, 0.07, 'triangle', 0.1), b(930, 930, 0.1, 'triangle', 0.1, 70)],
  /** Gold earned or spent. */
  coin: [b(1180, 1180, 0.05, 'square', 0.06), b(1570, 1570, 0.09, 'square', 0.06, 55)],
  /** A boss falls: little ascending fanfare. */
  victory: [523, 659, 784, 1047].map((f, i) => b(f, f, 0.16, 'triangle', 0.11, i * 130)),
  /** 💰 A gold mission lands: a light two-note glint (v0.79). */
  goldAppear: [b(1320, 1560, 0.08, 'sine', 0.07), b(1760, 2093, 0.12, 'sine', 0.06, 90)],
  /** 🎓 The Training Grounds appear: a grander rising arpeggio. */
  trainingAppear: [659, 831, 988, 1319, 1568].map((f, i) => b(f, f * 1.02, 0.2, 'triangle', 0.1, i * 110)),
  /** 💢 A mission expires: a low, blunt thump (v1.98). */
  hit: [b(150, 55, 0.16, 'triangle', 0.16), b(80, 50, 0.24, 'sine', 0.15, 10)],
  /** 💜 An EPIC appears (v3.22, per Javi): a proud three-note lift
   * with a high shimmer on top. */
  epic: [
    b(392, 392, 0.22, 'sine', 0.09),
    b(587, 587, 0.14, 'triangle', 0.10),
    b(740, 740, 0.14, 'triangle', 0.10, 110),
    b(880, 932, 0.30, 'triangle', 0.11, 220),
    b(1760, 1865, 0.24, 'sine', 0.05, 240),
  ],
  /** 🌟 A LEGENDARY appears (v3.22): the full fanfare — deep root,
   * rising arpeggio, and a held sparkling top. */
  legendary: [
    b(262, 262, 0.5, 'sine', 0.08),
    b(523, 523, 0.14, 'triangle', 0.10),
    b(659, 659, 0.14, 'triangle', 0.10, 110),
    b(784, 784, 0.14, 'triangle', 0.10, 220),
    b(1047, 1047, 0.16, 'triangle', 0.11, 330),
    b(1319, 1397, 0.5, 'triangle', 0.11, 440),
    b(2093, 2217, 0.4, 'sine', 0.05, 460),
  ],
}

/** One blip onto the graph. Envelope rules (hard-won, v1.70/74): start
 * from TRUE zero, 12ms linear attack, exponential decay, land on zero
 * before the oscillator stops — abrupt gain edges are audible POPS. */
export function scheduleBlip(ac: BaseAudioContext, dest: AudioNode, s: BlipSpec, t0: number, onEnded?: () => void) {
  const osc = ac.createOscillator()
  const gain = ac.createGain()
  osc.type = s.type
  osc.frequency.setValueAtTime(s.freqFrom, t0)
  if (s.freqTo !== s.freqFrom) {
    osc.frequency.exponentialRampToValueAtTime(s.freqTo, t0 + s.duration)
  }
  gain.gain.setValueAtTime(0, t0)
  gain.gain.linearRampToValueAtTime(s.volume, t0 + 0.012)
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + s.duration)
  gain.gain.linearRampToValueAtTime(0, t0 + s.duration + 0.01)
  osc.connect(gain)
  gain.connect(dest)
  osc.onended = () => {
    osc.disconnect()
    gain.disconnect()
    onEnded?.()
  }
  osc.start(t0)
  osc.stop(t0 + s.duration + 0.03)
  return () => {
    const t = ac.currentTime
    const level = gain.gain.value
    gain.gain.cancelScheduledValues(t)
    gain.gain.setValueAtTime(level, t)
    gain.gain.linearRampToValueAtTime(0, t + 0.012)
    try { osc.stop(t + 0.012) } catch { /* already ended */ }
  }
}

/** The shared master chain: a gentle limiter so music + sfx can never
 * sum past full scale (clipping IS a pop — the mobile culprit). Same
 * settings in the live bus and the audit render. */
export function makeMaster(ac: BaseAudioContext): AudioNode {
  const comp = ac.createDynamicsCompressor()
  comp.threshold.value = -8
  comp.knee.value = 4
  comp.ratio.value = 12
  comp.attack.value = 0.002
  comp.release.value = 0.12
  comp.connect(ac.destination)
  return comp
}
